import './IssueMembershipByPhoneSheet.css';

import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useIssueMembershipByPhone, useMembershipTypes } from '../../queries';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onIssued?: (result: { membershipUuid: string; customerCreated: boolean }) => void;
};

const normalizePhone = (raw: string): string => raw.replace(/[^\d+]/g, '').trim();

const IssueMembershipByPhoneSheet = ({ isOpen, onClose, onIssued }: Props) => {
  const typesQuery = useMembershipTypes(isOpen);
  const issueMutation = useIssueMembershipByPhone();
  const resetIssueMutation = issueMutation.reset;

  const [phone, setPhone] = useState('');
  const [tierUuid, setTierUuid] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the sheet closes so a re-open starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setPhone('');
      setTierUuid('');
      setStartsAt('');
      setError(null);
      resetIssueMutation();
    }
  }, [isOpen, resetIssueMutation]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const tiers = useMemo(
    () => (typesQuery.data?.items ?? []).filter((tier) => tier.isActive !== false),
    [typesQuery.data]
  );

  // Default to the lowest tier-level on open so the merchant has a sensible
  // starting selection. Only set if the user hasn't picked anything yet.
  useEffect(() => {
    if (!isOpen || tierUuid) return;
    if (tiers.length === 0) return;
    const sorted = [...tiers].sort((a, b) => (a.tierLevel ?? 0) - (b.tierLevel ?? 0));
    setTierUuid(sorted[0]!.membershipTypeUuid);
  }, [isOpen, tiers, tierUuid]);

  const canSubmit = useMemo(() => {
    const phoneOk = normalizePhone(phone).length >= 7;
    return phoneOk && Boolean(tierUuid) && !issueMutation.isPending;
  }, [phone, tierUuid, issueMutation.isPending]);

  if (!isOpen) return null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const cleanedPhone = normalizePhone(phone);
    if (cleanedPhone.length < 7) {
      setError('Enter a valid phone number');
      return;
    }
    if (!tierUuid) {
      setError('Pick a tier');
      return;
    }
    issueMutation.mutate(
      {
        phone: cleanedPhone,
        tierUuid,
        startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
      },
      {
        onSuccess: (result) => {
          onIssued?.({
            membershipUuid: result.membership.membershipUuid,
            customerCreated: result.customerCreated,
          });
          onClose();
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : 'Could not issue membership. Try again.';
          setError(message);
        },
      }
    );
  };

  return (
    <div className="issue-by-phone" role="dialog" aria-label="Issue membership" data-testid="issue-by-phone-sheet">
      <button
        type="button"
        className="issue-by-phone__scrim"
        onClick={onClose}
        aria-label="Close issue membership sheet"
      />
      <aside className="issue-by-phone__panel">
        <header className="issue-by-phone__header">
          <div>
            <h2 className="issue-by-phone__title">Issue membership</h2>
            <p className="issue-by-phone__subtitle">
              Enter a phone number — we&apos;ll find the customer, or create them on the fly.
            </p>
          </div>
          <button
            type="button"
            className="issue-by-phone__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        <form className="issue-by-phone__form" onSubmit={handleSubmit}>
          <label className="issue-by-phone__field">
            <span className="issue-by-phone__label">PHONE NUMBER</span>
            <input
              type="tel"
              autoFocus
              className="issue-by-phone__input"
              placeholder="+91 99887 76655"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              data-testid="issue-by-phone-phone"
            />
          </label>

          <label className="issue-by-phone__field">
            <span className="issue-by-phone__label">TIER</span>
            <select
              className="issue-by-phone__select"
              value={tierUuid}
              onChange={(event) => setTierUuid(event.target.value)}
              data-testid="issue-by-phone-tier"
            >
              <option value="">Pick a tier…</option>
              {tiers.map((tier) => (
                <option key={tier.membershipTypeUuid} value={tier.membershipTypeUuid}>
                  {tier.name}
                </option>
              ))}
            </select>
          </label>

          <label className="issue-by-phone__field">
            <span className="issue-by-phone__label">START DATE (OPTIONAL)</span>
            <input
              type="date"
              className="issue-by-phone__input"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
              data-testid="issue-by-phone-starts-at"
            />
          </label>

          {error ? (
            <div className="issue-by-phone__error" data-testid="issue-by-phone-error">{error}</div>
          ) : null}

          <footer className="issue-by-phone__footer">
            <button
              type="button"
              className="issue-by-phone__cancel"
              onClick={onClose}
              disabled={issueMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="issue-by-phone__submit"
              disabled={!canSubmit}
              data-testid="issue-by-phone-submit"
            >
              {issueMutation.isPending ? 'Issuing…' : 'Issue membership'}
            </button>
          </footer>
        </form>
      </aside>
    </div>
  );
};

export default IssueMembershipByPhoneSheet;
